/**
 * TDSP Pump — Phase 2 Vision page
 *
 * Public-facing announcement of the Trillion Dollar Spending Power Pump
 * (TDSP Pump), the first physical community-asset the proposed credit union
 * intends to build after its NCUA charter is granted. Written to educate +
 * convert cold Instagram traffic that lands here from the founder's video
 * posts into charter-supporter pledges.
 *
 * Compliance: this page is marketing of a *proposed* long-term vision only.
 * No part of this page implies the TDSP Pump exists, that pledge dollars go
 * directly to its construction, or that construction will happen if the
 * charter is not granted. Every CTA points to the escrowed, non-binding
 * charter-supporter pledge flow at /credit-union.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Download,
  Fuel,
  Hammer,
  MapPin,
  Quote,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const HERO = "/assets/tdsp-pump/hero-rendering.jpg";
const REVEAL_VIDEO = "/assets/tdsp-pump/tdsp-pump-reveal.mp4";
const BLUEPRINTS = [
  {
    src: "/assets/tdsp-pump/blueprint-site-layout.jpg",
    title: "Sheet 1 of 3 · Full Site & Interior Layout",
    detail:
      "Complete site plan — forecourt island, convenience store, kitchen, "
      + "ATM, outdoor seating, electrical panelboard cross-sections, and the "
      + "electrical room key plan.",
  },
  {
    src: "/assets/tdsp-pump/blueprint-utilities.jpg",
    title: "Sheet 2 of 3 · Kitchen, Bathrooms & Underground Fuel",
    detail:
      "Kitchen service-area utilities, ADA restrooms, bathroom plumbing & "
      + "sanitary blueprint, underground fuel tank installation detail, and "
      + "overhead canopy structural blueprint.",
  },
  {
    src: "/assets/tdsp-pump/blueprint-mep.jpg",
    title: "Sheet 3 of 3 · Mechanical · Electrical · Fuel · Plumbing",
    detail:
      "Comprehensive MEP overlay — fuel system, underground storage, "
      + "plumbing, electrical power & wiring, lighting system, site drainage, "
      + "storm catch basins, and the oil-water separator.",
  },
];

const PHASE_STEPS = [
  {
    icon: Users,
    label: "Phase 1 — Charter Pledges",
    status: "In Progress",
    body:
      "3,000 non-binding $100 pledges held in escrow at Citizen Trust Bank, "
      + "demonstrating a Well-Defined Local Community of Fulton + DeKalb "
      + "charter supporters.",
    cta: { to: "/credit-union", label: "Add Your Pledge" },
  },
  {
    icon: ShieldCheck,
    label: "Phase 1b — NCUA Form 9500",
    status: "Preparing",
    body:
      "Field-of-Membership Study, Business Plan, and Charter Application "
      + "submitted to the NCUA Office of Credit Union Resources & Expansion.",
    cta: null,
  },
  {
    icon: CheckCircle2,
    label: "Phase 2 — Charter Approval",
    status: "Target",
    body:
      "NCUA grants the Proposed Trillion Dollar Spending Power Federal Credit "
      + "Union's charter. Pledged capital converts into founding share "
      + "deposits and the credit union opens its doors to members.",
    cta: null,
  },
  {
    icon: Fuel,
    label: "Phase 2b — TDSP Pump",
    status: "Target",
    body:
      "First community-owned asset financed by the credit union: a modern "
      + "fuel station, kitchen, ATM, and outdoor commerce hub with four "
      + "pumps, two underground storage tanks, and a full MEP buildout.",
    cta: null,
  },
];

export default function TDSPPump() {
  useEffect(() => {
    document.title = "TDSP Pump · Phase 2 Vision — Black Dollars United";
  }, []);

  return (
    <div
      className="bg-bdu-bg text-bdu-cream min-h-screen"
      data-testid="tdsp-pump-page"
    >
      <Hero />
      <IntroSection />
      <RevealVideoSection />
      <RoadmapSection />
      <BlueprintSection />
      <FounderNoteSection />
      <ClosingCTA />
    </div>
  );
}

// ---------- Hero ----------

function Hero() {
  return (
    <section className="relative overflow-hidden min-h-[720px]">
      <div className="absolute inset-0">
        <img
          src={HERO}
          alt="Architectural rendering of the proposed TDSP Pump storefront with pumps on a left-side forecourt island and a clear entrance walkway on the right"
          className="w-full h-full object-cover"
          style={{ objectPosition: "88% center" }}
          data-testid="tdsp-pump-hero-image"
        />
        {/* Hard left-side obsidian panel for the copy; the rendering stays
            visible on the right third only. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, #0A0A0A 0%, #0A0A0A 35%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.15) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 60%, #0A0A0A 100%)",
          }}
        />
      </div>
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-28 pb-32">
        <div className="max-w-3xl">
          <div className="label-eyebrow flex items-center gap-2">
            <Sparkles size={12} className="text-bdu-gold" />
            Phase 2 · Vision Blueprint · Under Review
          </div>
          <h1
            className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-bdu-cream leading-[1.02]"
            data-testid="tdsp-pump-h1"
          >
            The <span className="text-bdu-gold italic">TDSP Pump.</span>
            <br />
            The first thing we own, together.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-bdu-cream/80 leading-relaxed max-w-2xl">
            A full-service fuel station, kitchen, ATM, and community commerce
            hub — the <span className="text-bdu-gold">first physical asset </span>
            the Proposed Trillion Dollar Spending Power Federal Credit Union
            intends to finance after its NCUA charter is granted.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/credit-union"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="tdsp-pump-cta-pledge"
            >
              Back the Charter — Pledge $100
              <ArrowRight size={16} />
            </Link>
            <a
              href="#blueprints"
              className="inline-flex items-center gap-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10 px-6 py-3 text-xs tracking-[0.2em] uppercase transition"
              data-testid="tdsp-pump-cta-blueprints"
            >
              <Hammer size={14} /> See the Blueprints
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Intro ----------

function IntroSection() {
  const pillars = [
    { icon: MapPin, label: "Owned by the Community", body: "A credit-union-financed asset, chartered under NCUA Chapter 2, Appendix B." },
    { icon: Fuel, label: "Four Pumps · Two Tanks", body: "Full fuel service with unleaded and premium underground storage, LED canopy, and modern dispenser hardware." },
    { icon: Hammer, label: "Engineered to Code", body: "Three-sheet MEP + site plan drawn with the construction-engineering team currently reviewing the design." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7">
          <div className="label-eyebrow">What We're Building</div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight">
            A functional blueprint for the first community-owned commerce hub.
          </h2>
          <div className="mt-7 space-y-5 text-bdu-cream/75 leading-relaxed text-[15px]">
            <p>
              Black Dollars United — Trillion Dollar Spending Power Fund, Inc.
              is organizing to realize a single, measurable vision: rebuild our
              neighborhoods into communities we own, operate, and control, and
              transfer wealth back to the people who generate it every day
              through their spending.
            </p>
            <p>
              The <span className="text-bdu-gold">Proposed Trillion Dollar Spending Power Federal Credit Union</span>
              {" "}is the financial engine. The <span className="text-bdu-gold">TDSP Pump</span>
              {" "}is the first physical asset that engine is designed to
              finance — a modern fuel station, full kitchen, in-house ATM, and
              outdoor community commerce space, drawn out in a three-sheet
              construction document set still being refined with our engineering
              team.
            </p>
            <p>
              Everything on this page is a <strong className="text-bdu-cream">vision under development</strong>,
              not an operating business. The one step that unlocks it is the
              NCUA charter — and the NCUA grants charters to communities that
              show up on paper. That's why 3,000 pledge signatures matter.
            </p>
          </div>
        </div>
        <div className="md:col-span-5 space-y-4">
          {pillars.map((p, i) => (
            <div
              key={i}
              className="border border-bdu-gold/20 bg-bdu-surface2 p-5"
              data-testid={`tdsp-pump-pillar-${i}`}
            >
              <div className="flex items-center gap-3">
                <p.icon size={16} className="text-bdu-gold" />
                <div className="text-sm font-bold text-bdu-cream">{p.label}</div>
              </div>
              <p className="mt-2 text-sm text-bdu-cream/65 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Reveal Video ----------

function RevealVideoSection() {
  return (
    <section className="bg-bdu-bg border-y border-bdu-gold/15" data-testid="tdsp-pump-reveal-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
        <div className="label-eyebrow flex items-center gap-2">
          <Sparkles size={12} className="text-bdu-gold" /> One-Minute Reveal
        </div>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight max-w-3xl">
          The TDSP Pump in sixty seconds.
        </h2>
        <p className="mt-5 text-bdu-cream/65 max-w-2xl leading-relaxed">
          A short reveal covering the storefront vision, each of the three
          construction-document sheets, and the single step that unlocks the
          build. Share-ready for Instagram Reels, TikTok, and YouTube Shorts.
        </p>

        <div className="mt-10 mx-auto max-w-[420px] border border-bdu-gold/25 bg-black overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.08)]">
          <video
            src={REVEAL_VIDEO}
            poster={HERO}
            controls
            playsInline
            preload="metadata"
            className="w-full aspect-[9/16] bg-black"
            data-testid="tdsp-pump-reveal-video"
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <a
            href={REVEAL_VIDEO}
            download="tdsp-pump-reveal.mp4"
            className="inline-flex items-center gap-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10 px-5 py-2.5 text-xs tracking-[0.2em] uppercase transition"
            data-testid="tdsp-pump-reveal-download"
          >
            <Download size={14} /> Download MP4 (1080×1920)
          </a>
        </div>

        <p className="mt-6 text-center text-[11px] text-bdu-cream/40 italic max-w-2xl mx-auto leading-relaxed">
          Share-only vertical cut. The TDSP Pump remains a proposed future
          community asset of the Proposed Trillion Dollar Spending Power
          Federal Credit Union — pending NCUA charter approval.
        </p>
      </div>
    </section>
  );
}

// ---------- Roadmap ----------

function RoadmapSection() {
  return (
    <section className="bg-bdu-surface border-y border-bdu-gold/15">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
        <div className="label-eyebrow flex items-center gap-2">
          <Compass size={12} className="text-bdu-gold" /> How We Get There
        </div>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight max-w-3xl">
          Phases 1 → 2. One sequence. No shortcuts.
        </h2>
        <p className="mt-5 text-bdu-cream/65 max-w-2xl leading-relaxed">
          Every step below is sequential. The charter approval in Phase 2 is the
          door — nothing physical gets built until the regulator opens it.
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="tdsp-pump-roadmap">
          {PHASE_STEPS.map((s, i) => (
            <div
              key={i}
              className="relative border border-bdu-gold/20 bg-bdu-bg p-6"
              data-testid={`tdsp-pump-phase-${i}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <s.icon size={16} className="text-bdu-gold" />
                  <div className="font-serif text-xl text-bdu-cream">{s.label}</div>
                </div>
                <span className="text-[10px] tracking-[0.22em] uppercase text-bdu-gold border border-bdu-gold/40 px-2 py-1">
                  {s.status}
                </span>
              </div>
              <p className="mt-4 text-sm text-bdu-cream/70 leading-relaxed">{s.body}</p>
              {s.cta && (
                <Link
                  to={s.cta.to}
                  className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-bdu-gold hover:text-bdu-cream transition"
                  data-testid={`tdsp-pump-phase-cta-${i}`}
                >
                  {s.cta.label} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Blueprint showcase ----------

function BlueprintSection() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="blueprints" className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
      <div className="label-eyebrow flex items-center gap-2">
        <Hammer size={12} className="text-bdu-gold" /> Construction Document Set
      </div>
      <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight max-w-3xl">
        Three sheets. One build-ready blueprint — still in refinement.
      </h2>
      <p className="mt-5 text-bdu-cream/65 max-w-2xl leading-relaxed">
        The construction-engineering team's current working set covers the full
        site plan, the kitchen and sanitary systems, and the complete
        mechanical / electrical / fuel / plumbing overlay. Click any sheet to
        inspect it full-screen.
      </p>

      <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="tdsp-pump-blueprints">
        {BLUEPRINTS.map((b, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setLightbox(b)}
            className="group text-left border border-bdu-gold/20 bg-bdu-surface2 hover:border-bdu-gold transition overflow-hidden"
            data-testid={`tdsp-pump-blueprint-${i}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-black">
              <img
                src={b.src}
                alt={b.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500"
              />
            </div>
            <div className="p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-gold">
                Sheet {i + 1}
              </div>
              <div className="mt-2 font-serif text-lg text-bdu-cream leading-snug">
                {b.title}
              </div>
              <p className="mt-2 text-xs text-bdu-cream/60 leading-relaxed">
                {b.detail}
              </p>
            </div>
          </button>
        ))}
      </div>

      <p className="mt-10 text-[11px] text-bdu-cream/40 italic max-w-2xl leading-relaxed">
        Working construction document — not yet stamped for permit. All
        dimensions, equipment schedules, and detail callouts remain subject to
        the licensed engineer-of-record's final signature prior to any
        permit application.
      </p>

      {lightbox && (
        <div
          className="fixed inset-0 bg-bdu-bg/95 z-50 flex items-center justify-center p-4 sm:p-10"
          onClick={() => setLightbox(null)}
          data-testid="tdsp-pump-blueprint-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-bdu-cream/80 hover:text-bdu-cream text-xs tracking-[0.2em] uppercase"
            data-testid="tdsp-pump-blueprint-close"
          >
            Close ✕
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.title}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </section>
  );
}

// ---------- Founder note ----------

function FounderNoteSection() {
  return (
    <section className="bg-bdu-surface border-y border-bdu-gold/15">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
        <div className="label-eyebrow">A Word From the Founder</div>
        <div className="mt-6 relative">
          <Quote
            size={60}
            className="absolute -top-6 -left-4 text-bdu-gold/15"
            strokeWidth={1}
          />
          <blockquote className="relative font-serif text-2xl sm:text-3xl text-bdu-cream leading-snug italic max-w-3xl">
            "Everybody is talking about pooling our money to build banks and
            build gas stations. Don't talk about it — <span className="not-italic text-bdu-gold">be about it.</span>
            {" "}Support the TDSP Fund at Citizen Trust Bank, support the
            proposed TDSP FCU charter, and let's go build the first one
            together. My team didn't want me talking about the TDSP Pump — but
            this is the vision, these are the plans, and we are already in
            Phase 2."
          </blockquote>
          <div className="mt-6 flex items-center gap-4 text-xs tracking-[0.2em] uppercase text-bdu-gold/80">
            <div className="h-px w-10 bg-bdu-gold/40" />
            Greg L. Bradford · Founder / CEO / President
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Closing CTA ----------

function ClosingCTA() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-28 text-center">
        <div className="label-eyebrow justify-center flex items-center gap-2">
          <ShieldCheck size={12} className="text-bdu-gold" /> The One Step That Unlocks Phase 2
        </div>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05]">
          Add your name to the <span className="text-bdu-gold italic">3,000</span>.
        </h2>
        <p className="mt-6 text-lg text-bdu-cream/75 max-w-2xl mx-auto leading-relaxed">
          A $100 non-binding pledge, held in escrow at Citizen Trust Bank. It
          cannot be withdrawn for any purpose other than the charter
          application — and is refunded in full if the NCUA does not grant our
          charter.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/credit-union"
            className="btn-gold inline-flex items-center gap-2"
            data-testid="tdsp-pump-closing-pledge"
          >
            Pledge $100 at Citizen Trust <ArrowRight size={16} />
          </Link>
          <Link
            to="/field-of-membership"
            className="inline-flex items-center gap-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10 px-6 py-3 text-xs tracking-[0.2em] uppercase transition"
            data-testid="tdsp-pump-closing-fom"
          >
            Check Your ZIP · Fulton + DeKalb
          </Link>
        </div>
        <p className="mt-12 text-[11px] text-bdu-cream/40 italic max-w-3xl mx-auto leading-relaxed">
          The TDSP Pump is a proposed future community asset of the Proposed
          Trillion Dollar Spending Power Federal Credit Union. The proposed
          credit union is not yet operational and is pending NCUA charter
          approval, and all charter-supporter pledges are non-binding
          expressions of interest held in a segregated escrow account at
          Citizen Trust Bank. Estimated construction cost figures referenced
          elsewhere are planning estimates only and are not final.
        </p>
      </div>
    </section>
  );
}
